// @ts-nocheck
import React, { FC, forwardRef, HTMLAttributes } from 'react';
import MuiSnackbar from '@material-ui/core/Snackbar';
import MuiSnackbarContent from '@material-ui/core/SnackbarContent';
import Slide from '@material-ui/core/Slide';
import { TransitionProps } from '@material-ui/core/transitions';
import Typography from '../Typography';
import Icon from '../Icon';
import CheckCircle from '../Icon/icons/filled/check-circle';
import InfoCircle from '../Icon/icons/filled/info-circle';
import ExclamationTriangle from '../Icon/icons/filled/exclamation-triangle';
import TimesCircle from '../Icon/icons/filled/times-circle';
import {
  SnackbarProps,
  SnackbarContentProps,
  Direction,
  Semantic,
} from './types';
import {
  useStyles,
  MessageWrapper,
  MessageContent,
  TitleStyle,
  IconWrapper,
} from './styles';

function TransitionLeft(props: TransitionProps) {
  return <Slide {...props} direction={Direction.LEFT} />;
}

function TransitionUp(props: TransitionProps) {
  return <Slide {...props} direction={Direction.UP} />;
}

function TransitionRight(props: TransitionProps) {
  return <Slide {...props} direction={Direction.RIGHT} />;
}

function TransitionDown(props: TransitionProps) {
  return <Slide {...props} direction={Direction.DOWN} />;
}

const transitionMap = {
  [Direction.LEFT]: TransitionLeft,
  [Direction.UP]: TransitionUp,
  [Direction.RIGHT]: TransitionRight,
  [Direction.DOWN]: TransitionDown,
};

const semanticIcon = {
  [Semantic.WARNING]: {
    icon: <Icon icon={ExclamationTriangle} />,
    color: '#F3973E',
  },
  [Semantic.INFO]: {
    icon: <Icon icon={InfoCircle} />,
    color: '#4DBAE9',
  },
  [Semantic.SUCCESS]: {
    icon: <Icon icon={CheckCircle} />,
    color: '#A0D45E',
  },
  [Semantic.ERROR]: {
    icon: <Icon icon={TimesCircle} />,
    color: '#EC3C63',
  },
};

const Content: FC<SnackbarContentProps> = forwardRef<
  HTMLAttributes<HTMLDivElement>,
  SnackbarContentProps
>(
  (
    { title, message, action, semantic, showSemanticIcon, MessageIcon },
    ref
  ) => {
    const classes = useStyles({ semantic, withTitle: !!title });
    const icon = semantic && semanticIcon[semantic].icon;
    const iconColor = semantic && semanticIcon[semantic].color;

    return (
      <MuiSnackbarContent
        ref={ref}
        message={
          <div className={MessageWrapper}>
            {showSemanticIcon && (
              <IconWrapper color={iconColor}>{icon}</IconWrapper>
            )}
            {!showSemanticIcon && MessageIcon && (
              <IconWrapper color={iconColor}>{MessageIcon}</IconWrapper>
            )}
            <div className={MessageContent}>
              {title && (
                <Typography
                  variant="h6"
                  weight="medium"
                  color="text.B6"
                  className={TitleStyle}
                >
                  {title}
                </Typography>
              )}
              <Typography variant="p" color="text.B6">
                {message}
              </Typography>
            </div>
          </div>
        }
        action={action}
        classes={classes}
      />
    );
  }
);

const Snackbar: FC<SnackbarProps> = forwardRef<
  HTMLAttributes<HTMLDivElement>,
  SnackbarProps
>(
  (
    {
      open,
      title,
      message,
      action,
      semantic,
      transitionDirection,
      MessageIcon,
      showSemanticIcon,
      onExited,
      ...restProps
    },
    ref
  ) => {
    return (
      <MuiSnackbar
        ref={ref}
        open={open}
        {...(transitionDirection && {
          TransitionComponent: transitionMap[transitionDirection],
        })}
        {...restProps}
      >
        <Content
          title={title}
          message={message}
          action={action}
          semantic={semantic}
          showSemanticIcon={showSemanticIcon}
          MessageIcon={MessageIcon}
        />
      </MuiSnackbar>
    );
  }
);

export default Snackbar;
